import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db } from '../firebase/config';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { useCRM } from '../context/CRMContext';

// ─── Merge tags ────────────────────────────────────────────────────────────────
const TEMPLATE_TAGS = [
  { label:'First Name',  tag:'{{firstName}}' },
  { label:'Last Name',   tag:'{{lastName}}' },
  { label:'Full Name',   tag:'{{fullName}}' },
  { label:'Program',     tag:'{{program}}' },
  { label:'Course',      tag:'{{course}}' },
  { label:'Phone',       tag:'{{phone}}' },
  { label:'Email',       tag:'{{email}}' },
  { label:'Counselor',   tag:'{{counselorName}}' },
  { label:'Company',     tag:'{{companyName}}' },
  { label:'Date',        tag:'{{date}}' },
];

// ─── Step type config ──────────────────────────────────────────────────────────
const STEP_TYPES = {
  whatsapp: { label:'WhatsApp Message', icon:'💬', color:'#25d366', bg:'#f0fdf4', border:'#bbf7d0' },
  email:    { label:'Send Email',       icon:'📧', color:'#2563eb', bg:'#eff6ff', border:'#bfdbfe' },
  wait:     { label:'Wait / Delay',     icon:'⏱',  color:'#d97706', bg:'#fffbeb', border:'#fef3c7' },
  sms:      { label:'Send SMS',         icon:'📱', color:'#7c3aed', bg:'#f5f3ff', border:'#ede9fe' },
};

// ─── Trigger config ────────────────────────────────────────────────────────────
const TRIGGERS = [
  { id:'new_lead',      label:'New Lead Added',     icon:'👤', desc:'Fires when any new lead is created in the CRM' },
  { id:'stage_change',  label:'Stage Change',        icon:'🔄', desc:'Fires when a lead moves into a specific pipeline stage' },
  { id:'form_submit',   label:'Web Form Submission', icon:'📋', desc:'Fires when a student submits a web form' },
  { id:'manual',        label:'Manual / On-Demand',  icon:'▶',  desc:'You manually enroll students into this automation' },
];

// ─── Tag picker helper ─────────────────────────────────────────────────────────
function TagPicker({ textareaRef, value, onChange }) {
  const insertTag = (tag) => {
    const ta = textareaRef?.current;
    if (!ta) { onChange(value + tag); return; }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const next = value.slice(0, start) + tag + value.slice(end);
    onChange(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + tag.length, start + tag.length); }, 10);
  };
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Insert Tag</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {TEMPLATE_TAGS.map(t => (
          <button key={t.tag} type="button" onClick={() => insertTag(t.tag)}
            style={{ padding: '2px 8px', borderRadius: 5, border: '1px solid #c4b5fd', background: '#f5f3ff', color: '#7c3aed', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Highlight {{tags}} in preview text ───────────────────────────────────────
function TagHighlight({ text }) {
  if (!text) return null;
  const parts = text.split(/(\{\{[^}]+\}\})/g);
  return (
    <>
      {parts.map((p, i) =>
        /^\{\{/.test(p)
          ? <span key={i} style={{ background: '#ede9fe', color: '#7c3aed', borderRadius: 4, padding: '0 4px', fontSize: 11, fontWeight: 700 }}>{p}</span>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

// ─── Step Card (add/edit) ──────────────────────────────────────────────────────
function StepEditor({ step, index, onChange, onDelete, templates, pipelineStages, integrations }) {
  const taRef = useRef(null);
  const cfg = STEP_TYPES[step.type] || STEP_TYPES.whatsapp;

  // Derive configured sender info for warnings / display
  const smtp = integrations?.smtp || {};
  const emailConfigured = !!(smtp.smtpHost && smtp.smtpUser && smtp.smtpPass);
  const emailFrom = smtp.smtpFromEmail || smtp.smtpUser || '';
  const emailFromName = smtp.smtpFromName || '';

  // WhatsApp: check any connected WA provider
  const waProviders = ['waai_meta', 'wati', 'interakt', 'gupshup', 'twilio'];
  const connectedWa = waProviders.find(p => integrations?.[p]?.connected);
  const waConfigured = !!connectedWa;
  const waPhone = connectedWa ? (integrations[connectedWa]?.displayPhone || integrations[connectedWa]?.phoneNumberId || integrations[connectedWa]?.phone || '') : '';

  return (
    <div style={{ background: cfg.bg, border: `1.5px solid ${cfg.border}`, borderRadius: 14, padding: 18, position: 'relative' }}>
      {/* Step number + type selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: cfg.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
          {index + 1}
        </div>
        <select value={step.type} onChange={e => onChange({ ...step, type: e.target.value, body: '', subject: '', templateId: '' })}
          style={{ border: `1.5px solid ${cfg.border}`, borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 700, color: cfg.color, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
          {Object.entries(STEP_TYPES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
        <button onClick={onDelete}
          style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 7, border: 'none', background: 'rgba(239,68,68,0.1)', color: '#dc2626', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
          ✕ Remove
        </button>
      </div>

      {/* Wait step */}
      {step.type === 'wait' && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Wait</span>
          <input type="number" min="1" value={step.delay || 1} onChange={e => onChange({ ...step, delay: parseInt(e.target.value) || 1 })}
            style={{ width: 64, padding: '7px 10px', borderRadius: 8, border: `1.5px solid ${cfg.border}`, fontSize: 14, fontWeight: 700, textAlign: 'center', fontFamily: 'inherit' }} />
          <select value={step.unit || 'days'} onChange={e => onChange({ ...step, unit: e.target.value })}
            style={{ padding: '7px 10px', borderRadius: 8, border: `1.5px solid ${cfg.border}`, fontSize: 13, fontFamily: 'inherit' }}>
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>before sending the next step</span>
        </div>
      )}

      {/* WhatsApp / SMS step */}
      {(step.type === 'whatsapp' || step.type === 'sms') && (
        <div>
          {/* Sender info banner */}
          {step.type === 'whatsapp' && (
            waConfigured ? (
              <div style={{ padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 12, color: '#166534', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                💬 Sending from: <strong>{waPhone || connectedWa?.replace('waai_meta','Meta WhatsApp').replace('wati','WATI').replace('interakt','Interakt').replace('gupshup','Gupshup').replace('twilio','Twilio') || 'Connected WhatsApp'}</strong>
              </div>
            ) : (
              <div style={{ padding: '8px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 10 }}>
                ⚠️ No WhatsApp integration connected. Go to <strong>Settings → Integrations → WhatsApp</strong> to connect one before this step can send.
              </div>
            )
          )}
          {templates.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Use saved template (optional)</label>
              <select value={step.templateId || ''} onChange={e => {
                const t = templates.find(x => x.id === e.target.value);
                onChange({ ...step, templateId: e.target.value, body: t?.body || step.body });
              }} style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: `1.5px solid ${cfg.border}`, fontSize: 13, fontFamily: 'inherit' }}>
                <option value="">— Custom message below —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </div>
          )}
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Message Body *</label>
          <TagPicker textareaRef={taRef} value={step.body || ''} onChange={v => onChange({ ...step, body: v, templateId: '' })} />
          <textarea ref={taRef} value={step.body || ''} onChange={e => onChange({ ...step, body: e.target.value, templateId: '' })}
            rows={4} placeholder={`Type your ${step.type === 'whatsapp' ? 'WhatsApp' : 'SMS'} message…`}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1.5px solid ${cfg.border}`, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff' }} />
        </div>
      )}

      {/* Email step */}
      {step.type === 'email' && (
        <div>
          {/* Sender info banner */}
          {emailConfigured ? (
            <div style={{ padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12, color: '#1e40af', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              📧 Sending from: <strong>{emailFromName ? `${emailFromName} <${emailFrom}>` : emailFrom}</strong>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: '#60a5fa' }}>via Settings → SMTP</span>
            </div>
          ) : (
            <div style={{ padding: '8px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 10 }}>
              ⚠️ No email (SMTP) configured. Go to <strong>Settings → Integrations → Email (SMTP)</strong> to set up a sender before this step can send.
            </div>
          )}
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Subject *</label>
          <input value={step.subject || ''} onChange={e => onChange({ ...step, subject: e.target.value })}
            placeholder="e.g. Welcome to {{companyName}} — next steps"
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${cfg.border}`, fontSize: 13, fontFamily: 'inherit', marginBottom: 10, boxSizing: 'border-box', background: '#fff' }} />
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Email Body *</label>
          <TagPicker textareaRef={taRef} value={step.body || ''} onChange={v => onChange({ ...step, body: v })} />
          <textarea ref={taRef} value={step.body || ''} onChange={e => onChange({ ...step, body: e.target.value })}
            rows={5} placeholder="Type your email body…"
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1.5px solid ${cfg.border}`, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff' }} />
        </div>
      )}
    </div>
  );
}

// ─── Main Automations page ─────────────────────────────────────────────────────
export default function Automations() {
  const { currentUser, companyId, leads, pipelineStages, webForms, integrations } = useCRM();

  const [automations,   setAutomations]   = useState([]);
  const [templates,     setTemplates]     = useState([]);
  const [enrollments,   setEnrollments]   = useState([]);
  const [selectedId,    setSelectedId]    = useState(null);
  const [editing,       setEditing]       = useState(false);
  const [form,          setForm]          = useState(null);
  const [showEnroll,    setShowEnroll]    = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [toast,         setToast]         = useState('');

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // ── Firestore subscriptions ────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(
      query(collection(db, 'companies', companyId, 'automations'), orderBy('createdAt', 'desc')),
      snap => setAutomations(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const q = query(collection(db, 'waTemplates'), where('companyId', '==', companyId));
    return onSnapshot(q, snap => {
      const tmpl = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      tmpl.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setTemplates(tmpl);
    }, () => {});
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !selectedId) return;
    return onSnapshot(
      query(collection(db, 'companies', companyId, 'automationLogs'), where('automationId', '==', selectedId)),
      snap => setEnrollments(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
  }, [companyId, selectedId]);

  // ── Selected automation ────────────────────────────────────────────────────
  const selected = useMemo(() => automations.find(a => a.id === selectedId) || null, [automations, selectedId]);

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const blankForm = () => ({
    name: '', description: '',
    trigger: 'new_lead', triggerStage: '', triggerFormId: '',
    status: 'active',
    steps: [],
  });

  const startNew = () => {
    setForm(blankForm());
    setSelectedId(null);
    setEditing(true);
  };

  const startEdit = (auto) => {
    setForm({ ...auto });
    setSelectedId(auto.id);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(null);
  };

  const saveAutomation = async () => {
    if (!form.name.trim() || !companyId) return;
    setSaving(true);
    try {
      if (form.id) {
        const { id, createdAt, ...data } = form;
        await updateDoc(doc(db, 'companies', companyId, 'automations', id), { ...data, updatedAt: serverTimestamp() });
        showToast('Automation updated ✓');
      } else {
        const ref = await addDoc(collection(db, 'companies', companyId, 'automations'), {
          ...form, companyId,
          createdBy: currentUser?.id || '',
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          stats: { enrolled: 0, sent: 0, opened: 0, replied: 0 },
        });
        setSelectedId(ref.id);
        showToast('Automation created ✓');
      }
      setEditing(false);
      setForm(null);
    } finally { setSaving(false); }
  };

  const deleteAutomation = async (id) => {
    if (!window.confirm('Delete this automation? This cannot be undone.')) return;
    await deleteDoc(doc(db, 'companies', companyId, 'automations', id));
    if (selectedId === id) { setSelectedId(null); setEditing(false); setForm(null); }
    showToast('Deleted');
  };

  const toggleStatus = async (auto) => {
    const next = auto.status === 'active' ? 'paused' : 'active';
    await updateDoc(doc(db, 'companies', companyId, 'automations', auto.id), { status: next });
    showToast(next === 'active' ? 'Automation resumed ✓' : 'Automation paused');
  };

  // ── Enroll student manually ────────────────────────────────────────────────
  const enrollStudent = async (leadId) => {
    if (!companyId || !selectedId) return;
    const already = enrollments.find(e => e.leadId === leadId && e.status !== 'completed');
    if (already) { alert('Student is already enrolled in this automation.'); return; }
    const lead = (leads || []).find(l => l.id === leadId);
    await addDoc(collection(db, 'companies', companyId, 'automationLogs'), {
      automationId: selectedId,
      leadId, leadName: [lead?.firstName, lead?.lastName].filter(Boolean).join(' ') || lead?.name || '',
      companyId, status: 'active', currentStep: 0,
      startedAt: serverTimestamp(), stepLogs: [],
    });
    // bump enrolled count
    const auto = automations.find(a => a.id === selectedId);
    if (auto) {
      await updateDoc(doc(db, 'companies', companyId, 'automations', selectedId), {
        'stats.enrolled': (auto.stats?.enrolled || 0) + 1,
      });
    }
    showToast('Student enrolled ✓');
  };

  // ── Step helpers ────────────────────────────────────────────────────────────
  const addStep = () => {
    setForm(f => ({ ...f, steps: [...(f.steps || []), { id: Date.now().toString(), type: 'whatsapp', body: '', delay: 1, unit: 'days' }] }));
  };

  const updateStep = (idx, step) => {
    setForm(f => {
      const steps = [...(f.steps || [])];
      steps[idx] = step;
      return { ...f, steps };
    });
  };

  const deleteStep = (idx) => {
    setForm(f => ({ ...f, steps: (f.steps || []).filter((_, i) => i !== idx) }));
  };

  const moveStep = (idx, dir) => {
    setForm(f => {
      const steps = [...(f.steps || [])];
      const to = idx + dir;
      if (to < 0 || to >= steps.length) return f;
      [steps[idx], steps[to]] = [steps[to], steps[idx]];
      return { ...f, steps };
    });
  };

  // ── Enroll modal ────────────────────────────────────────────────────────────
  const [enrollSearch, setEnrollSearch] = useState('');
  const enrollableLeads = useMemo(() => {
    const enrolled = new Set(enrollments.map(e => e.leadId));
    return (leads || [])
      .filter(l => {
        const n = [l.firstName, l.lastName, l.name].filter(Boolean).join(' ').toLowerCase();
        return n.includes(enrollSearch.toLowerCase());
      })
      .slice(0, 30);
  }, [leads, enrollments, enrollSearch]);

  const inp = { width: '100%', boxSizing: 'border-box', border: '1.5px solid rgba(0,0,0,0.12)', borderRadius: 8, fontSize: 13, padding: '9px 12px', fontFamily: 'inherit', outline: 'none', background: '#fafafa' };

  const triggerLabel = (t) => TRIGGERS.find(x => x.id === t)?.label || t;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', background: 'var(--bg-primary)', fontFamily: "'Inter', sans-serif" }}>

      {/* ── Left sidebar: automation list ─────────────────────── */}
      <div style={{ width: 280, borderRight: '1px solid rgba(0,0,0,0.08)', background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 16px 12px', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>⚡ Automations</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>Set up message sequences for students</div>
        </div>
        {isAdmin && (
          <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
            <button onClick={startNew}
              style={{ width: '100%', padding: '10px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              + New Automation
            </button>
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {automations.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 16px', color: '#94a3b8', fontSize: 12 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>⚡</div>
              No automations yet.<br />Click "+ New" to create one.
            </div>
          )}
          {automations.map(auto => {
            const isSelected = selectedId === auto.id && !editing;
            const statusColor = auto.status === 'active' ? '#059669' : auto.status === 'paused' ? '#d97706' : '#94a3b8';
            return (
              <div key={auto.id} onClick={() => { setSelectedId(auto.id); setEditing(false); setForm(null); }}
                style={{ padding: '12px 12px', borderRadius: 10, marginBottom: 4, cursor: 'pointer', background: isSelected ? '#f5f3ff' : 'transparent', border: isSelected ? '1.5px solid #c4b5fd' : '1.5px solid transparent', transition: 'all 0.1s' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 6 }}>{auto.name}</div>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: statusColor + '18', color: statusColor, textTransform: 'uppercase', flexShrink: 0 }}>
                    {auto.status || 'draft'}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  {TRIGGERS.find(t => t.id === auto.trigger)?.icon} {triggerLabel(auto.trigger)}
                  {' · '}{(auto.steps || []).length} steps
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                  👥 {auto.stats?.enrolled || 0} enrolled · 📨 {auto.stats?.sent || 0} sent
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Main content ──────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Empty state */}
        {!selectedId && !editing && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: '#94a3b8' }}>
            <div style={{ fontSize: 56 }}>⚡</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#475569' }}>Student Automation Engine</div>
            <div style={{ fontSize: 13, textAlign: 'center', maxWidth: 360, lineHeight: 1.6 }}>
              Build multi-step message sequences that automatically send WhatsApp messages, emails, and SMS to students based on triggers like "new lead", "stage change", or manually.
            </div>
            {isAdmin && (
              <button onClick={startNew}
                style={{ marginTop: 8, padding: '11px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                + Create First Automation
              </button>
            )}
          </div>
        )}

        {/* ── Editor mode ─────────────────────────────────────── */}
        {editing && form && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <button onClick={cancelEdit} style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid rgba(0,0,0,0.1)', background: '#fff', cursor: 'pointer', fontSize: 12 }}>← Back</button>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{form.id ? 'Edit Automation' : 'New Automation'}</div>
            </div>

            <div style={{ maxWidth: 680, margin: '0 auto', display: 'grid', gap: 20 }}>

              {/* Name + description */}
              <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.07)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>📋 Automation Details</div>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Name *</label>
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. New Student Welcome Sequence"
                      style={inp} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Description</label>
                    <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      rows={2} placeholder="What does this automation do?"
                      style={{ ...inp, resize: 'vertical' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Status</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {['active','paused','draft'].map(s => (
                        <button key={s} onClick={() => setForm(f => ({ ...f, status: s }))}
                          style={{ padding: '6px 14px', borderRadius: 8, border: `1.5px solid ${form.status === s ? '#7c3aed' : 'rgba(0,0,0,0.1)'}`, background: form.status === s ? '#7c3aed' : '#fff', color: form.status === s ? '#fff' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Trigger */}
              <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.07)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>🎯 Trigger — When does this fire?</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {TRIGGERS.map(trig => (
                    <div key={trig.id} onClick={() => setForm(f => ({ ...f, trigger: trig.id }))}
                      style={{ padding: '12px 14px', borderRadius: 12, border: `1.5px solid ${form.trigger === trig.id ? '#7c3aed' : 'rgba(0,0,0,0.08)'}`, background: form.trigger === trig.id ? '#f5f3ff' : '#f8fafc', cursor: 'pointer' }}>
                      <div style={{ fontSize: 16, marginBottom: 4 }}>{trig.icon}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: form.trigger === trig.id ? '#7c3aed' : '#374151' }}>{trig.label}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, lineHeight: 1.4 }}>{trig.desc}</div>
                    </div>
                  ))}
                </div>
                {form.trigger === 'stage_change' && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Pipeline Stage *</label>
                    <select value={form.triggerStage || ''} onChange={e => setForm(f => ({ ...f, triggerStage: e.target.value }))}
                      style={{ ...inp, height: 38, padding: '0 12px' }}>
                      <option value="">Select a stage…</option>
                      {(pipelineStages || []).map(s => <option key={s.id || s.name} value={s.name}>{s.name}</option>)}
                    </select>
                  </div>
                )}

                {form.trigger === 'form_submit' && (
                  <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                        Which Form? <span style={{ color: '#94a3b8', fontWeight: 400 }}>(leave blank = all forms)</span>
                      </label>
                      {(webForms || []).length === 0 ? (
                        <div style={{ padding: '10px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                          ⚠️ No web forms found. Create a form in <strong>Web Forms</strong> first.
                        </div>
                      ) : (
                        <select value={form.triggerFormId || ''} onChange={e => setForm(f => ({ ...f, triggerFormId: e.target.value }))}
                          style={{ ...inp, height: 38, padding: '0 12px' }}>
                          <option value="">All web forms</option>
                          {(webForms || []).map(wf => <option key={wf.id} value={wf.id}>{wf.name}</option>)}
                        </select>
                      )}
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                        {form.triggerFormId
                          ? `Fires when a lead is created from "${(webForms||[]).find(w=>w.id===form.triggerFormId)?.name || 'selected form'}"`
                          : 'Fires when a lead is created from any of your web forms'}
                      </div>
                    </div>
                    <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 12, color: '#166534' }}>
                      ✅ The lead's name, phone, email and all form fields are available as merge tags in your steps.
                    </div>
                  </div>
                )}
              </div>

              {/* Steps */}
              <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>📨 Steps — What happens?</div>
                  <button onClick={addStep}
                    style={{ padding: '7px 14px', borderRadius: 9, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    + Add Step
                  </button>
                </div>

                {(form.steps || []).length === 0 && (
                  <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8', background: '#f8fafc', borderRadius: 10, border: '2px dashed rgba(0,0,0,0.08)' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>📨</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>No steps yet</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Add a WhatsApp, Email, or Wait step to build your sequence</div>
                    <button onClick={addStep} style={{ marginTop: 14, padding: '8px 18px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                      + Add First Step
                    </button>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {(form.steps || []).map((step, idx) => (
                    <div key={step.id || idx}>
                      <StepEditor
                        step={step}
                        index={idx}
                        onChange={s => updateStep(idx, s)}
                        onDelete={() => deleteStep(idx)}
                        templates={templates}
                        pipelineStages={pipelineStages || []}
                        integrations={integrations || {}}
                      />
                      {/* Move up/down */}
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
                        {idx > 0 && <button onClick={() => moveStep(idx, -1)} style={{ padding: '2px 8px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.1)', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#64748b' }}>↑ Move up</button>}
                        {idx < (form.steps || []).length - 1 && <button onClick={() => moveStep(idx, 1)} style={{ padding: '2px 8px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.1)', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#64748b' }}>↓ Move down</button>}
                      </div>
                      {/* Connector line */}
                      {idx < (form.steps || []).length - 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
                          <div style={{ width: 2, height: 20, background: 'linear-gradient(#7c3aed44,#7c3aed22)', borderRadius: 1 }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Save */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={cancelEdit} style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid rgba(0,0,0,0.1)', background: '#fff', fontSize: 14, cursor: 'pointer' }}>Cancel</button>
                <button onClick={saveAutomation} disabled={saving || !form.name.trim()}
                  style={{ padding: '10px 28px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                  {saving ? 'Saving…' : form.id ? '✓ Save Changes' : '✓ Create Automation'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── View mode ───────────────────────────────────────── */}
        {selected && !editing && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{selected.name}</div>
                  <span style={{
                    padding: '3px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                    background: selected.status === 'active' ? '#dcfce7' : selected.status === 'paused' ? '#fef3c7' : '#f1f5f9',
                    color: selected.status === 'active' ? '#166534' : selected.status === 'paused' ? '#92400e' : '#64748b',
                  }}>
                    {selected.status === 'active' ? '● Active' : selected.status === 'paused' ? '⏸ Paused' : '✏ Draft'}
                  </span>
                </div>
                {selected.description && <div style={{ fontSize: 13, color: '#64748b' }}>{selected.description}</div>}
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  {TRIGGERS.find(t => t.id === selected.trigger)?.icon} {triggerLabel(selected.trigger)}
                  {selected.trigger === 'stage_change' && selected.triggerStage && ` → "${selected.triggerStage}"`}
                  {selected.trigger === 'form_submit' && (
                    selected.triggerFormId
                      ? ` → "${(webForms||[]).find(w=>w.id===selected.triggerFormId)?.name || 'Specific form'}"`
                      : ' → All web forms'
                  )}
                </div>
              </div>
              {isAdmin && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowEnroll(true)}
                    style={{ padding: '8px 16px', borderRadius: 9, border: 'none', background: '#25d366', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    👤 Enroll Student
                  </button>
                  <button onClick={() => startEdit(selected)}
                    style={{ padding: '8px 14px', borderRadius: 9, border: '1.5px solid rgba(0,0,0,0.1)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => toggleStatus(selected)}
                    style={{ padding: '8px 14px', borderRadius: 9, border: `1.5px solid ${selected.status === 'active' ? '#d97706' : '#059669'}`, background: selected.status === 'active' ? 'rgba(217,119,6,0.08)' : 'rgba(5,150,105,0.08)', color: selected.status === 'active' ? '#d97706' : '#059669', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {selected.status === 'active' ? '⏸ Pause' : '▶ Resume'}
                  </button>
                  <button onClick={() => deleteAutomation(selected.id)}
                    style={{ padding: '8px 14px', borderRadius: 9, border: '1.5px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.06)', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}>Delete</button>
                </div>
              )}
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
              {[
                { label:'Enrolled', value: selected.stats?.enrolled || 0, icon:'👥', color:'#7c3aed' },
                { label:'Messages Sent', value: selected.stats?.sent || 0, icon:'📨', color:'#2563eb' },
                { label:'Opened', value: selected.stats?.opened || 0, icon:'👁', color:'#059669' },
                { label:'Replied', value: selected.stats?.replied || 0, icon:'💬', color:'#d97706' },
              ].map(s => (
                <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.07)', textAlign: 'center' }}>
                  <div style={{ fontSize: 22 }}>{s.icon}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>

              {/* Flow preview */}
              <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.07)' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>📨 Sequence Steps</div>
                {(selected.steps || []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8', background: '#f8fafc', borderRadius: 10, border: '2px dashed rgba(0,0,0,0.08)' }}>
                    No steps configured. <button onClick={() => startEdit(selected)} style={{ color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Edit to add steps.</button>
                  </div>
                ) : (
                  <div>
                    {/* Trigger bubble */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, background: '#f0fdf4', border: '1.5px solid #bbf7d0', marginBottom: 4 }}>
                      <div style={{ fontSize: 20 }}>{TRIGGERS.find(t => t.id === selected.trigger)?.icon || '🎯'}</div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>TRIGGER</div>
                        <div style={{ fontSize: 13, color: '#0f172a' }}>{triggerLabel(selected.trigger)}{selected.triggerStage ? ` → "${selected.triggerStage}"` : ''}</div>
                      </div>
                    </div>

                    {(selected.steps || []).map((step, idx) => {
                      const cfg = STEP_TYPES[step.type] || STEP_TYPES.whatsapp;
                      return (
                        <div key={step.id || idx}>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={{ width: 2, height: 20, background: `${cfg.color}44` }} />
                          </div>
                          <div style={{ background: cfg.bg, border: `1.5px solid ${cfg.border}`, borderRadius: 12, padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: step.type !== 'wait' ? 8 : 0 }}>
                              <div style={{ width: 24, height: 24, borderRadius: '50%', background: cfg.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>
                                {idx + 1}
                              </div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {cfg.icon} {cfg.label}
                              </div>
                            </div>
                            {step.type === 'wait' && (
                              <div style={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                                Wait {step.delay} {step.unit || 'days'}
                              </div>
                            )}
                            {(step.type === 'whatsapp' || step.type === 'sms' || step.type === 'email') && step.subject && (
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>📌 {step.subject}</div>
                            )}
                            {(step.type === 'whatsapp' || step.type === 'sms' || step.type === 'email') && step.body && (
                              <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.55, maxHeight: 60, overflow: 'hidden', position: 'relative' }}>
                                <TagHighlight text={step.body.slice(0, 120) + (step.body.length > 120 ? '…' : '')} />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                      <div style={{ width: 2, height: 20, background: '#7c3aed44' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, background: '#f8fafc', border: '1.5px dashed rgba(0,0,0,0.1)' }}>
                      <div style={{ fontSize: 16 }}>✅</div>
                      <div style={{ fontSize: 13, color: '#94a3b8' }}>Automation complete</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Enrolled students panel */}
              <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.07)' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>👥 Enrolled Students ({enrollments.length})</div>
                {enrollments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 12px', color: '#94a3b8', fontSize: 12 }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>👥</div>
                    No students enrolled yet.
                    {selected.trigger === 'manual' && isAdmin && (
                      <><br /><button onClick={() => setShowEnroll(true)} style={{ marginTop: 10, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Enroll Students</button></>
                    )}
                  </div>
                ) : enrollments.map(e => {
                  const statusCol = e.status === 'completed' ? '#059669' : e.status === 'failed' ? '#dc2626' : '#7c3aed';
                  return (
                    <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg,#7c3aed,#3b82f6)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {(e.leadName || '?').charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.leadName || 'Unknown'}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>
                          Step {(e.currentStep || 0) + 1} / {(selected.steps || []).length} · {e.status || 'active'}
                        </div>
                      </div>
                      <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: statusCol + '18', color: statusCol, fontWeight: 700 }}>
                        {e.status || 'active'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Enroll modal ────────────────────────────────────────── */}
      {showEnroll && selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 18, padding: 24, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>👤 Enroll Students</div>
              <button onClick={() => { setShowEnroll(false); setEnrollSearch(''); }} style={{ padding: '4px 10px', borderRadius: 7, border: 'none', background: 'rgba(0,0,0,0.07)', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Search for a student to add to "<strong>{selected.name}</strong>"</div>
            <input value={enrollSearch} onChange={e => setEnrollSearch(e.target.value)}
              placeholder="Search by name…"
              style={{ ...inp, marginBottom: 12 }} />
            <div style={{ maxHeight: 320, overflowY: 'auto', display: 'grid', gap: 6 }}>
              {enrollableLeads.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0', fontSize: 13 }}>No students found</div>}
              {enrollableLeads.map(lead => {
                const name = [lead.firstName, lead.lastName, lead.name].filter(Boolean).join(' ') || lead.email || 'Unknown';
                const already = enrollments.some(e => e.leadId === lead.id && e.status !== 'completed');
                return (
                  <div key={lead.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)', background: already ? '#f8fafc' : '#fff' }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#7c3aed,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700 }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{lead.email || lead.phone || ''}</div>
                    </div>
                    {already ? (
                      <span style={{ fontSize: 11, color: '#059669', fontWeight: 700 }}>✓ Enrolled</span>
                    ) : (
                      <button onClick={() => enrollStudent(lead.id)}
                        style={{ padding: '5px 12px', borderRadius: 7, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        Enroll
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#0f172a', color: '#fff', padding: '12px 20px', borderRadius: 12, fontSize: 13, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', zIndex: 200 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
